import { useCallback, useMemo, useState } from 'react';
import {
  CategoryType,
  CategoryTypeFilterList,
  CategoryTypeString,
  ContentOrderType,
  ContentOrderTypeListString,
} from '@diritto/common/content/constants';
import { TrackerExtraInfo, TrackerPosition, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { CategoryFilterBox } from 'ui-components/common/CategoryFilterBox';
import { FilterBox } from 'ui-components/common/FilterBox';
import { VerticalLine } from 'ui-components/common/VerticalLine';
import { FilterMenuType } from 'ui-components/common/FilterItemList';
import { useTrackingState } from 'contexts/TrackingProvider';
import { FeatureFlagName } from '@diritto/common/system/constants';
import { useFeatureFlagConfig } from 'ui-hooks/useFeatureFlagConfig';

export type ContentSearchParams = {
  categoryType?: CategoryType;
  contentOrderType?: ContentOrderType;
  onlySerialized?: boolean;
};

const ContentFilterOrdertTypeList = [
  {
    title: ContentOrderTypeListString[ContentOrderType.Best],
    type: ContentOrderType.Best,
  },
  {
    title: ContentOrderTypeListString[ContentOrderType.LatestEpisodeOpenedAt],
    type: ContentOrderType.LatestEpisodeOpenedAt,
  },
  {
    title: ContentOrderTypeListString[ContentOrderType.Id],
    type: ContentOrderType.Id,
  },
];

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.Search,
};

interface ContentSearchOrderTabProps {
  contentOrderType?: ContentOrderType;
  totalCount?: number;
  categoryType: CategoryType;
  onlySerialized: boolean;
  onChange: (contentSearchParams: ContentSearchParams) => void;
}

export const ContentSearchOrderTab = ({
  contentOrderType,
  totalCount,
  categoryType,
  onlySerialized,
  onChange,
}: ContentSearchOrderTabProps) => {
  const { tracker } = useTrackingState();
  const { isActiveFlag } = useFeatureFlagConfig();
  const isFanFictionEnabled = isActiveFlag(FeatureFlagName.FanFiction);
  const categoryTypeFilterList = useMemo(
    () =>
      isFanFictionEnabled
        ? CategoryTypeFilterList
        : CategoryTypeFilterList.filter(type => type !== CategoryType.FanFiction),
    [isFanFictionEnabled],
  );
  const [orderBy, setOrder] = useState(contentOrderType);

  const handleOrderItemClick = useCallback(
    (value: ContentOrderType) => {
      tracker.track(TrackingEventType.SetOrderFilter, { orderBy: value, trackerExtraInfo });
      setOrder(value);
      onChange({ contentOrderType: value });
    },
    [onChange, tracker],
  );

  const handleOnlySerializedClick = useCallback(() => {
    tracker.track(TrackingEventType.SetCompletedOnlyFilter, { enabled: !onlySerialized, trackerExtraInfo });
    onChange({ onlySerialized: !onlySerialized });
  }, [onChange, onlySerialized, tracker]);

  const handleCategoryTypeClick = useCallback(
    (value: CategoryType) => {
      tracker.track(TrackingEventType.SetCategoryFilter, { categoryType: value, trackerExtraInfo });
      onChange({ categoryType: value });
    },
    [onChange, tracker],
  );

  const filterOrderItems = useMemo(
    () =>
      ContentFilterOrdertTypeList.map(item => ({
        title: item.title,
        checked: item.type === orderBy,
        onClick: () => handleOrderItemClick(item.type),
      })),
    [orderBy, handleOrderItemClick],
  );

  const filterCategoryItems = useMemo(
    () =>
      categoryTypeFilterList.map(item => ({
        title: CategoryTypeString[item],
        value: item,
        checked: item === categoryType,
        onClick: () => handleCategoryTypeClick(item),
      })),
    [categoryTypeFilterList, categoryType, handleCategoryTypeClick],
  );

  const filterOrderMenuGroups = useMemo(
    () => [
      { key: 'order', type: FilterMenuType.CircleRadioButton, items: filterOrderItems },
      {
        key: 'serialized',
        type: FilterMenuType.CircleCheckBoxButton,
        items: [{ title: '연재중', checked: onlySerialized, onClick: handleOnlySerializedClick }],
      },
    ],
    [filterOrderItems, onlySerialized, handleOnlySerializedClick],
  );

  return (
    <div className="flex flex-row items-center justify-between py-4">
      <div className="flex flex-row items-center gap-1">
        <p className="text-xxs font-medium leading-tight">{`검색결과`}</p>
        <p className="text-xxs font-medium leading-tight">{`${totalCount}`}</p>
      </div>
      <div className="flex flex-row items-center justify-center space-x-2">
        <CategoryFilterBox items={filterCategoryItems} categoryType={categoryType} />
        <VerticalLine height={12} />
        <FilterBox filterMenuGroups={filterOrderMenuGroups} isFiltering={onlySerialized} />
      </div>
    </div>
  );
};
